class StyleDeduplicator {
  private cache = new Map<string, HTMLStyleElement>();
  private observer: MutationObserver | null = null;
  private isActive = false;

  private generateHash(content: string): string {
    let hash = 0;
    if (content.length === 0) return hash.toString();

    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    return Math.abs(hash).toString(36);
  }

  private handleStyleElement(styleElement: HTMLStyleElement): void {
    // Skip JSS/MUI style tags. they use empty <style> tags with attributes as placeholders
    // for dynamic style injection. These tags are NOT duplicates and should never be removed
    // as they serve as containers for JSS to inject component-specific styles.
    // Example: <style data-jss="" data-meta="MuiBox"></style>
    if (styleElement.hasAttributes()) {
      return;
    }

    const content = (styleElement.textContent || "").trim();
    const hash = this.generateHash(content);

    if (!content) {
      return;
    }

    if (this.cache.has(hash)) {
      const previousStyleElement = this.cache.get(hash);

      // Remove the previously added style element, not the new one
      this.safeRemoveElement(previousStyleElement);
    }
    this.cache.set(hash, styleElement);
  }

  private safeRemoveElement(element?: Element): void {
    try {
      element?.remove();
    } catch (error) {
      console.error("[StyleOptimizer] Error removing element:", error);
    }
  }

  private processExistingStyles(): void {
    const existingStyles = document.head.querySelectorAll("style");
    existingStyles.forEach((style) =>
      this.handleStyleElement(style as HTMLStyleElement)
    );
  }

  public start(): void {
    if (this.isActive) {
      console.warn("[StyleOptimizer] Already active");
      return;
    }

    this.isActive = true;

    this.processExistingStyles();

    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "childList" &&
          mutation.target === document.head
        ) {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;

              if (element.tagName.toLowerCase() === "style") {
                this.handleStyleElement(element as HTMLStyleElement);
              }
            }
          }
        }
      }
    });

    this.observer.observe(document.head, { childList: true });
  }

  public stop(): void {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.cache.clear();
  }
}

const styleDeduplicator = new StyleDeduplicator();

export { styleDeduplicator };
